import type { TFunction } from "i18next";

import type { PersonaOpportunityStatus } from "../api/persona-opportunity-sets.api";

/**
 * Fixed Opportunities-tab status sort order (KTD8):
 * NEW → SCRIPTS_GENERATED → BRIEFS_GENERATED → SCRIPTS_AND_BRIEFS_GENERATED → SENT_TO_VENDOR.
 */
export const PERSONA_OPPORTUNITY_STATUS_SORT_ORDER: readonly PersonaOpportunityStatus[] = [
  "NEW",
  "SCRIPTS_GENERATED",
  "BRIEFS_GENERATED",
  "SCRIPTS_AND_BRIEFS_GENERATED",
  "SENT_TO_VENDOR",
] as const;

const STATUS_ORDINAL: Record<PersonaOpportunityStatus, number> = {
  NEW: 0,
  SCRIPTS_GENERATED: 1,
  BRIEFS_GENERATED: 2,
  SCRIPTS_AND_BRIEFS_GENERATED: 3,
  SENT_TO_VENDOR: 4,
};

const STATUS_LABEL: Record<PersonaOpportunityStatus, { key: string; fallback: string }> = {
  NEW: { key: "personaOpportunities.active.statusNew", fallback: "New" },
  SCRIPTS_GENERATED: {
    key: "personaOpportunities.active.statusScriptsGenerated",
    fallback: "Scripts generated",
  },
  BRIEFS_GENERATED: {
    key: "personaOpportunities.active.statusBriefsGenerated",
    fallback: "Briefs generated",
  },
  SCRIPTS_AND_BRIEFS_GENERATED: {
    key: "personaOpportunities.active.statusScriptsAndBriefsGenerated",
    fallback: "Scripts + briefs generated",
  },
  SENT_TO_VENDOR: {
    key: "personaOpportunities.active.statusSentToVendor",
    fallback: "Sent to production partner",
  },
};

export function personaOpportunityStatusOrdinal(status: PersonaOpportunityStatus): number {
  return STATUS_ORDINAL[status] ?? Number.MAX_SAFE_INTEGER;
}

/** Compare two statuses for ascending Opportunities-tab sort. */
export function comparePersonaOpportunityStatus(
  a: PersonaOpportunityStatus,
  b: PersonaOpportunityStatus,
): number {
  return personaOpportunityStatusOrdinal(a) - personaOpportunityStatusOrdinal(b);
}

/** Localized label for an opportunity row status (card, table, filters). */
export function personaOpportunityStatusLabel(
  t: TFunction,
  status: PersonaOpportunityStatus,
): string {
  const entry = STATUS_LABEL[status];
  return entry ? t(entry.key, entry.fallback) : status;
}
